// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { value: boolean | null; onChange: (value: boolean | null) => void; label: string; disabled?: boolean };

/**
 * One of a unit's features: yes, no, or nobody said — three states, drawn as three marks.
 *
 * A checkbox has two, so it would quietly turn every unfilled record into "this unit has no garden".
 * A company creates unit shells and fills them in over the following week; most features are
 * genuinely unknown for most of that week, and the form has to be able to say so rather than guess.
 *
 * The marks carry the meaning and the words only back them up: a tick, a cross, and a dash. At a
 * glance down a column of fifteen features, a row's answer is its shape, not its text.
 */
export default function FeatureToggle({ value, onChange, label, disabled }: Props) {
    const t = useTranslation();
    const options = [
        { key: "yes", state: true as boolean | null, icon: "check" as const, title: t.properties.yes },
        { key: "no", state: false as boolean | null, icon: "close" as const, title: t.properties.no },
        { key: "unknown", state: null, icon: "clear" as const, title: t.properties.unknown },
    ];

    const answered = value === null ? "unknown" : value ? "yes" : "no";

    return (
        <div className={`feature-toggle feature-toggle--${answered}`}>
            <span className="feature-toggle__label" title={label}>
                {label}
            </span>

            <div className="feature-toggle__marks" role="group" aria-label={label}>
                {options.map((option) => (
                    <button
                        key={option.key}
                        type="button"
                        className={`feature-toggle__mark feature-toggle__mark--${option.key}${value === option.state ? " feature-toggle__mark--on" : ""}`}
                        onClick={() => onChange(option.state)}
                        disabled={disabled}
                        aria-pressed={value === option.state}
                        title={option.title}
                    >
                        <Icon name={option.icon} size={14} />
                    </button>
                ))}
            </div>
        </div>
    );
}
